import Vue from "vue";
import Router from "@router/router";
import { log } from "@utilities";
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";

const env = process.env.VUE_APP_MODE;
const request_id_pattern = /"request_id"\s*:\s*(?:"([^"]+)"|(\d+))/;
const eventId_pattern = /"(?:eventId|event_id)"\s*:\s*(?:"([^"]+)"|(\d+))/;
const TELESCOPE_BASE = process.env.VUE_APP_TELESCOPE_BASE || "";
const SILENT_STATUS = 511;

function inject_breadcrumb_data(data, { request_id, eventId }) {
  const result = { ...data };
  if (eventId) {
    result.eventId = eventId;
  }
  if (request_id) {
    result.request_id = request_id;
    if (TELESCOPE_BASE) {
      result.telescope = `${TELESCOPE_BASE}?tag=request_id%3A${request_id}`;
    }
  }
  return result;
}

/*
 * SentryInit(siteOptions)
 *
 * siteOptions (all optional):
 *   tracePropagationTargets  â€” array of strings/regexps controlling which outgoing requests
 *                              receive the sentry-trace header for distributed tracing.
 *                              Defaults to relative paths only (/^\//), which is the safest
 *                              option: prevents CORS preflight on third-party APIs (HERE, Stripe, etc.)
 *
 *   replayOnError            â€” boolean, default false
 *                              If true: enables error-only replay (buffer mode).
 *                              Records into a 60s ring buffer; uploads only when an error occurs.
 *                              networkCaptureBodies is always false to prevent fetch blocking.
 */
export let sentryEnabled = false;

export default function SentryInit(siteOptions = {}) {
  log("[sentry.js] - sentry init");

  const { VUE_APP_SENTRY_DSN: dsn } = process.env;
  if (!dsn) {
    return;
  }
  const {
    tracePropagationTargets = [
      /^https:\/\/([^.]+\.)?senna\.vadobay\.com/,
      /^\//,
    ],
    replayOnError = false,
  } = siteOptions;

  const integrations = [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(Router),
      tracePropagationTargets,
    }),
  ];

  if (replayOnError) {
    integrations.push(
      Sentry.replayIntegration({
        // Disables response.text() interception â€” prevents fetch/XHR blocking (issue #13996)
        networkCaptureBodies: false,
        networkDetailAllowUrls: [],
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        mutationLimit: 5000,
      }),
    );
  }

  Sentry.init({
    Vue,
    environment: env,
    dsn,
    release: `${process.env.VUE_APP_NAME}@${process.env.VUE_APP_TAZIO_VERSION}`,
    integrations,

    tracesSampleRate: env === "production" ? 0.1 : 1.0,
    debug: env !== "production",

    // Noise filtering: ignore known benign errors and browser extension noise
    ignoreErrors: [
      // Non-Error objects thrown as exceptions (third-party code, unactionable)
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      "Object captured as promise rejection",

      // Network: user navigated away mid-request, offline, or ad-blocker
      "NetworkError when attempting to fetch resource",
      "Failed to fetch",
      "Load failed",
      "Network Error",
      "Network request failed",
      "The operation was aborted",
      "AbortError",
      "Request aborted",
      /^TypeError: (cancelled|cancelado|annulÃ©)$/, // Safari cancellation in multiple locales

      // Cross-origin script errors (no useful info available)
      /^Script error\.?$/,

      // Chunk loading (stale cache after deploy, not a bug)
      "ChunkLoadError",
      /Loading chunk [\d]+ failed/,
      /Loading CSS chunk [\d]+ failed/,

      // ResizeObserver: benign browser warning, not application errors
      /ResizeObserver loop/,

      // Vue Router navigation: expected flow control, not errors
      "NavigationDuplicated",
      /Avoided redundant navigation/,
      /Navigation cancelled/,
      /Navigation aborted/,

      // Storage quota (user-environment issue)
      "QuotaExceededError",

      // Security restrictions (cross-origin iframe, private browsing)
      "SecurityError",
    ],
    denyUrls: [
      // Browser extensions â€” errors from these are never actionable
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category !== 'xhr') return breadcrumb;
      const { xhr = {} } = hint || {};
      const responseText = xhr.responseText || (typeof xhr.response === 'string' && xhr.response) || '';
      const responseObj = typeof xhr.response === 'object' && xhr.response;

      let request_id = null;
      let eventId = null;

      if (responseText) {
        const requestIdMatch = request_id_pattern.exec(responseText);
        if (requestIdMatch) request_id = requestIdMatch[1] || requestIdMatch[2];
        const eventIdMatch = eventId_pattern.exec(responseText);
        if (eventIdMatch) eventId = eventIdMatch[1] || eventIdMatch[2];
      } else if (responseObj) {
        request_id = responseObj.request_id || null;
        eventId = responseObj.eventId || responseObj.event_id || null;
        if (typeof request_id === 'number') request_id = String(request_id);
        if (typeof eventId === 'number') eventId = String(eventId);
      }

      if (request_id || eventId) {
        breadcrumb.data = inject_breadcrumb_data(breadcrumb.data, { request_id, eventId });
      }

      return breadcrumb;
    },
    beforeSend(event, hint) {
      // 511 is the backend's "silent error" convention (optional call skipped), never a fault
      const { originalException } = hint || {};
      const { response = {} } = originalException || {};
      if (response.status === SILENT_STATUS) return null;

      // Drop exception events with no stack trace (noise from third-party scripts)
      // Non-exception events (messages) are passed through as-is
      if (event.exception) {
        const values = event.exception.values;
        if (!values || !values[0] || !values[0].stacktrace) return null;
      }
      return event;
    },

    // Vue specific
    trackComponents: [],
    hooks: [],
    logErrors: env !== "production",
    attachProps: true,
    attachStacktrace: false,

    // Session Replay: session recording always off; error-only buffer active only if replayOnError: true
    replaysSessionSampleRate: 0,
    ...(replayOnError ? { replaysOnErrorSampleRate: 0.5 } : {}),
  });

  sentryEnabled = true;
}